import type { Game } from "../types/Game";

/**
 * Placeholder game catalog. One entry per archetype from CLAUDE.md. Replaced
 * by real published games once templates ship and the publishing pipeline is
 * wired up to Cloudflare R2.
 *
 * Design note: the `thumbnail` field is intentionally a solid color + emoji.
 * This lets the portal render a credible grid WITHOUT sourcing art, and keeps
 * design's hands free for the real visual pass.
 *
 * Values for `plays`, `rating`, and `publishedAt` are fabricated but
 * deliberately coherent — higher play counts tend to have higher ratings,
 * older games have more plays, etc. This makes the portal's sort/filter UX
 * feel real during development and demos.
 */
export const placeholderGames: Game[] = [
	{
		id: "1",
		slug: "monsterdex",
		title: "Monsterdex",
		creator: "@pixelpal",
		thumbnail: { bg: "#1a3d2a", fg: "#00ff88", emoji: "🦖" },
		archetype: "creature-collector",
		plays: 148_293,
		rating: 4.6,
		description:
			"Catch, train, and battle 150 original monsters across a sprawling open world. Build your dream team and challenge the Elite.",
		tags: ["collecting", "turn-based", "adventure", "rpg"],
		playUrl: "#",
		featured: true,
		trending: true,
		publishedAt: "2026-02-14",
	},
	{
		id: "2",
		slug: "neon-rush",
		title: "Neon Rush",
		creator: "@zapkid",
		thumbnail: { bg: "#2a0a3d", fg: "#ff00cc", emoji: "🏃" },
		archetype: "endless-runner",
		plays: 892_104,
		rating: 4.8,
		description:
			"Sprint through a neon cyberpunk city, dodging drones and grinding rails at 60fps. One-touch controls, infinite replay.",
		tags: ["arcade", "one-touch", "fast-paced", "high-score"],
		playUrl: "#",
		featured: true,
		trending: true,
		publishedAt: "2025-11-02",
	},
	{
		id: "3",
		slug: "cubefall",
		title: "Cubefall",
		creator: "@tetrobit",
		thumbnail: { bg: "#0a2a3d", fg: "#00ccff", emoji: "🧱" },
		archetype: "block-puzzle",
		plays: 412_667,
		rating: 4.5,
		description:
			"Drop blocks, clear lines, chain combos. A cozy puzzle game you can pick up for 30 seconds or 30 minutes.",
		tags: ["puzzle", "casual", "relaxing", "brain"],
		playUrl: "#",
		featured: true,
		publishedAt: "2025-12-18",
	},
	{
		id: "4",
		slug: "gemquest",
		title: "Gemquest: Crown Jewels",
		creator: "@candycoder",
		thumbnail: { bg: "#3d0a2a", fg: "#ff4466", emoji: "💎" },
		archetype: "match-3",
		plays: 276_451,
		rating: 4.4,
		description:
			"Match three, crack the grid, rescue the kingdom. 100 levels of cascading combos and juicy feedback.",
		tags: ["match-3", "levels", "juicy", "casual"],
		playUrl: "#",
		trending: true,
		publishedAt: "2026-01-09",
	},
	{
		id: "5",
		slug: "tiny-town",
		title: "Tiny Town",
		creator: "@mergequeen",
		thumbnail: { bg: "#2d3d0a", fg: "#ffcc00", emoji: "🏘️" },
		archetype: "merge",
		plays: 91_882,
		rating: 4.3,
		description:
			"Merge identical items to build a bustling village. Discover new items, complete quests, decorate your town.",
		tags: ["merge", "cozy", "collection", "progression"],
		playUrl: "#",
		publishedAt: "2026-03-01",
	},
	{
		id: "6",
		slug: "galactic-bakery",
		title: "Galactic Bakery",
		creator: "@idleforge",
		thumbnail: { bg: "#3d2a0a", fg: "#ffcc00", emoji: "🍪" },
		archetype: "idle-clicker",
		plays: 523_019,
		rating: 4.7,
		description:
			"Click to bake cookies. Hire alien staff. Unlock prestige bonuses. Watch your bakery conquer the galaxy while you sleep.",
		tags: ["idle", "clicker", "prestige", "incremental"],
		playUrl: "#",
		trending: true,
		publishedAt: "2025-10-21",
	},
	{
		id: "7",
		slug: "frontline-tactics",
		title: "Frontline Tactics",
		creator: "@towerlord",
		thumbnail: { bg: "#1d0a3d", fg: "#00ccff", emoji: "🏰" },
		archetype: "tower-defense",
		plays: 187_442,
		rating: 4.5,
		description:
			"Place towers, upgrade weapons, hold the line against 50 waves of invaders. Seven tower types, 12 enemy archetypes.",
		tags: ["tower-defense", "strategy", "waves", "upgrades"],
		playUrl: "#",
		publishedAt: "2026-02-28",
	},
	{
		id: "8",
		slug: "skyhop",
		title: "Skyhop",
		creator: "@jumpjam",
		thumbnail: { bg: "#0a3d3d", fg: "#00ccff", emoji: "🦘" },
		archetype: "platformer",
		plays: 334_217,
		rating: 4.6,
		description:
			"Tight platforming across 30 hand-crafted levels. Precision jumps, speedrun ghosts, and no cheap deaths — we promise.",
		tags: ["platformer", "precision", "speedrun", "skill"],
		playUrl: "#",
		featured: true,
		publishedAt: "2026-01-24",
	},
	{
		id: "9",
		slug: "lost-grove",
		title: "Lost Grove",
		creator: "@ruinrunner",
		thumbnail: { bg: "#0a3d1a", fg: "#00ff88", emoji: "🗡️" },
		archetype: "top-down-rpg",
		plays: 212_805,
		rating: 4.6,
		description:
			"Explore an ancient forest full of puzzles, dungeons, and forgotten bosses. A love letter to classic top-down adventures.",
		tags: ["rpg", "adventure", "puzzles", "exploration"],
		playUrl: "#",
		trending: true,
		publishedAt: "2025-12-05",
	},
	{
		id: "10",
		slug: "voidrift",
		title: "Voidrift.io",
		creator: "@arenaace",
		thumbnail: { bg: "#1a0a2d", fg: "#ff00cc", emoji: "⚔️" },
		archetype: "io-arena",
		plays: 1_204_881,
		rating: 4.7,
		description:
			"Real-time multiplayer arena. Grow, consume, dominate the leaderboard. One tap to play, 90-second matches.",
		tags: ["multiplayer", "io", "competitive", "quick-play"],
		playUrl: "#",
		trending: true,
		publishedAt: "2025-09-14",
	},
	{
		id: "11",
		slug: "pocket-chirpy",
		title: "Pocket Chirpy",
		creator: "@petwarden",
		thumbnail: { bg: "#3d3d0a", fg: "#ffcc00", emoji: "🐣" },
		archetype: "virtual-pet",
		plays: 66_422,
		rating: 4.2,
		description:
			"Feed it. Bathe it. Dress it up. Teach it tricks. Your own pocket creature that grows with you across 30 days.",
		tags: ["virtual-pet", "daily", "cozy", "cute"],
		playUrl: "#",
		publishedAt: "2026-03-12",
	},
	{
		id: "12",
		slug: "glam-studio",
		title: "Glam Studio",
		creator: "@glamlab",
		thumbnail: { bg: "#3d0a3d", fg: "#ff00cc", emoji: "👗" },
		archetype: "fashion",
		plays: 178_334,
		rating: 4.4,
		description:
			"Style outfits for weekly themes, walk the runway, earn votes. Hundreds of clothing pieces and free daily drops.",
		tags: ["fashion", "creative", "social", "daily"],
		playUrl: "#",
		publishedAt: "2026-02-02",
	},
	{
		id: "13",
		slug: "snack-empire",
		title: "Snack Empire",
		creator: "@tycoontess",
		thumbnail: { bg: "#3d1a0a", fg: "#ffcc00", emoji: "🍔" },
		archetype: "tycoon",
		plays: 245_918,
		rating: 4.5,
		description:
			"Run a roadside snack stand and grow it into a food empire. Hire, restock, expand, repeat.",
		tags: ["tycoon", "management", "progression", "cozy"],
		playUrl: "#",
		publishedAt: "2026-01-15",
	},
	{
		id: "14",
		slug: "battlecards-origins",
		title: "Battlecards: Origins",
		creator: "@deckforge",
		thumbnail: { bg: "#0a1a3d", fg: "#00ccff", emoji: "🃏" },
		archetype: "card-battle",
		plays: 308_774,
		rating: 4.6,
		description:
			"Build decks from 180 unique cards. Climb ranked ladder, face smart AI, and master five elemental factions.",
		tags: ["cards", "deckbuilding", "strategy", "pvp"],
		playUrl: "#",
		featured: true,
		publishedAt: "2025-12-27",
	},
	{
		id: "15",
		slug: "night-shift",
		title: "Night Shift",
		creator: "@dreadwatch",
		thumbnail: { bg: "#1a0a14", fg: "#ff4466", emoji: "👻" },
		archetype: "survival-horror",
		plays: 129_447,
		rating: 4.3,
		description:
			"You're the only guard on duty. Watch the cameras. Lock the doors. Survive until 6 AM. Do NOT look behind you.",
		tags: ["horror", "survival", "atmosphere", "tense"],
		playUrl: "#",
		publishedAt: "2026-02-20",
	},
	{
		id: "16",
		slug: "hillblitz",
		title: "Hillblitz",
		creator: "@dirtdash",
		thumbnail: { bg: "#3d2a1a", fg: "#ffcc00", emoji: "🏎️" },
		archetype: "racing",
		plays: 417_902,
		rating: 4.5,
		description:
			"Physics-based hill racing. Tune your engine, stunt for bonus cash, and conquer 40 chaotic tracks.",
		tags: ["racing", "physics", "upgrades", "arcade"],
		playUrl: "#",
		trending: true,
		publishedAt: "2025-11-30",
	},
	{
		id: "17",
		slug: "cozy-harvest",
		title: "Cozy Harvest",
		creator: "@farmbloom",
		thumbnail: { bg: "#1a3d0a", fg: "#00ff88", emoji: "🌻" },
		archetype: "farming-sim",
		plays: 263_188,
		rating: 4.7,
		description:
			"Tend crops, raise animals, befriend villagers, and restore a neglected farm. Cozy vibes, four full seasons.",
		tags: ["farming", "cozy", "life-sim", "relaxing"],
		playUrl: "#",
		featured: true,
		publishedAt: "2025-12-12",
	},
	{
		id: "18",
		slug: "vocabular",
		title: "Vocabular",
		creator: "@lexiquest",
		thumbnail: { bg: "#2a2a3d", fg: "#00ccff", emoji: "🔠" },
		archetype: "word-trivia",
		plays: 554_006,
		rating: 4.6,
		description:
			"A fresh 5-letter word puzzle every day. Share your result without spoilers. Simple, elegant, addictive.",
		tags: ["word", "daily", "puzzle", "share"],
		playUrl: "#",
		trending: true,
		publishedAt: "2025-10-08",
	},
	{
		id: "19",
		slug: "dice-kingdom",
		title: "Dice Kingdom",
		creator: "@boardbard",
		thumbnail: { bg: "#2a1a3d", fg: "#ff00cc", emoji: "🎲" },
		archetype: "board-game",
		plays: 98_112,
		rating: 4.2,
		description:
			"Roll dice, move pawns, claim territory. A party board game for 1–4 players (with bots that don't feel cheap).",
		tags: ["board", "dice", "party", "multiplayer"],
		playUrl: "#",
		publishedAt: "2026-02-11",
	},
	{
		id: "20",
		slug: "pixel-plaza",
		title: "Pixel Plaza",
		creator: "@pixelparty",
		thumbnail: { bg: "#3d0a1a", fg: "#ff00cc", emoji: "🎉" },
		archetype: "social-hangout",
		plays: 84_553,
		rating: 4.1,
		description:
			"A persistent neon hangout. Customize your avatar, meet friends, play mini-games. No goals — just vibes.",
		tags: ["social", "avatar", "hangout", "multiplayer"],
		playUrl: "#",
		publishedAt: "2026-03-20",
	},
];

/**
 * Find a single game by slug. Returns undefined if not found.
 */
export function findGameBySlug(slug: string): Game | undefined {
	return placeholderGames.find((g) => g.slug === slug);
}

/**
 * Return games matching a plain-text query — checks title, description, tags,
 * creator, and archetype label. Case-insensitive.
 */
export function searchGames(query: string): Game[] {
	const q = query.trim().toLowerCase();
	if (!q) return [];
	return placeholderGames.filter((g) => {
		const haystack = [
			g.title,
			g.description,
			g.creator,
			g.archetype,
			...g.tags,
		]
			.join(" ")
			.toLowerCase();
		return haystack.includes(q);
	});
}

/**
 * Return N related games — same archetype first, then popular fallback.
 * Excludes the current game.
 */
export function relatedGames(slug: string, limit = 4): Game[] {
	const current = findGameBySlug(slug);
	if (!current) return [];
	const sameArchetype = placeholderGames.filter(
		(g) => g.slug !== slug && g.archetype === current.archetype,
	);
	if (sameArchetype.length >= limit) return sameArchetype.slice(0, limit);
	const fallback = placeholderGames
		.filter((g) => g.slug !== slug && g.archetype !== current.archetype)
		.sort((a, b) => b.plays - a.plays);
	return [...sameArchetype, ...fallback].slice(0, limit);
}
